import * as actionTypes from "./actionTypes";
import axios from "../../axios";

const authenticationStarted = () => {
  return { type: actionTypes.AUTH_START };
};

const authenticationSuccess = (token, username) => {
  return {
    type: actionTypes.AUTH_SUCCESS,
    token: token,
    username: username
  };
};

const authenticationFail = (error) => {
  return { type: actionTypes.AUTH_FAIL, error: error };
};

const authenticationRequires2FA = (userId, redirectUrl) => {
  return { type: actionTypes.AUTH_REQUIRES_2FA, userId: userId, redirectUrl: redirectUrl };
};

const authentication2FAStarted = () => {
  return { type: actionTypes.AUTH_2FA_START };
};

export const authenticate = (username, password, redirectUrl = null) => {
  return (dispatch) => {
    dispatch(authenticationStarted());

    const payload = { username: username, password: password };
    if (redirectUrl) {
      payload.redirectUrl = redirectUrl;
    }

    axios.post("/auth/login", payload)
      .then((response) => {
        if (redirectUrl && response.data.sessionCode) {
          axios
            .get("/auth/validate-session-code", {
              params: { code: response.data.sessionCode }
            })
            .then((validateResponse) => {
              const finalRedirectUrl = validateResponse.data.redirectUrl || redirectUrl;

              // Authenticate using the validated token,
              handleAuthenticationSuccess(dispatch, validateResponse, null);

              // For redirect login, send back the sessionCode from /auth/login
              const separator = finalRedirectUrl.includes("?") ? "&" : "?";
              window.location.href = `${finalRedirectUrl}${separator}code=${encodeURIComponent(response.data.sessionCode)}`;
            })
            .catch((err) => {
              if (err.response) {
                dispatch(
                  authenticationFail(
                    err.response.data?.message || "Authentication failed while validating session."
                  )
                );
              } else {
                dispatch(authenticationFail("Network error during session validation."));
              }
            });
          return;
        }

        if (response.data.requiresTwoFactor) {
          dispatch(authenticationRequires2FA(response.data.userId, redirectUrl));
          return;
        }

        handleAuthenticationSuccess(dispatch, response, redirectUrl);
      })
      .catch((err) => {
        if (err.response) {
          switch (err.response.status) {
            case 401:
              dispatch(authenticationFail("Incorrect email address or password."));
              break;
            case 403:
              dispatch(authenticationFail("Only users with Technical role in the organization can access this page"));
              break;
            case 429:
              dispatch(authenticationFail(err.response.data?.error || "Too many login attempts. Please try again later."));
              break;
            default: 
              dispatch(authenticationFail("Authentication failed with code " + err.response.status + ".")); 
              break;
          }
        } else {
          dispatch(authenticationFail("Network error. Please check your connection."));
        }
      });
  };
};

export const verify2FA = (userId, code, redirectUrl = null) => {
  return (dispatch) => {
    dispatch(authentication2FAStarted());

    const payload = { userId: userId, code: code };
    if (redirectUrl) {
      payload.redirectUrl = redirectUrl;
    }

    axios.post("/auth/verify-2fa", payload)
      .then((response) => {
        // Redirect login flow with 2FA: backend returns a sessionCode instead of a token
        if (redirectUrl && response.data.sessionCode) {
          axios
            .get("/auth/validate-session-code", {
              params: { code: response.data.sessionCode }
            })
            .then((validateResponse) => {
              const finalRedirectUrl = validateResponse.data.redirectUrl || redirectUrl;

              handleAuthenticationSuccess(dispatch, validateResponse, null);

              // Send the sessionCode from /auth/verify-2fa
              const separator = finalRedirectUrl.includes("?") ? "&" : "?";
              window.location.href = `${finalRedirectUrl}${separator}code=${encodeURIComponent(
                response.data.sessionCode
              )}`;
            })
            .catch((err) => {
              if (err.response) {
                dispatch(
                  authenticationFail(
                    err.response.data?.message || "Authentication failed while validating session."
                  )
                );
              } else {
                dispatch(authenticationFail("Network error during session validation."));
              }
            });
          return;
        }

        // Regular 2FA flow without redirectUrl: backend returns token directly
        handleAuthenticationSuccess(dispatch, response, redirectUrl);
      })
      .catch((err) => {
        if (err.response) {
          switch (err.response.status) {
            case 401:
              dispatch(authenticationFail("Invalid 2FA code. Please try again."));
              break;
            case 400:
              dispatch(authenticationFail(err.response.data?.message || "Invalid request."));
              break;
            case 429:
              dispatch(authenticationFail(err.response.data?.error || "Too many attempts. Please try again later."));
              break;
            default:
              dispatch(authenticationFail("Verification failed with code " + err.response.status + "."));
              break;
          }
        } else if (!err.status) {
          dispatch(authenticationFail("Backend currently unavailable."));
        } else {
          dispatch(authenticationFail("Verification failed. Please try again."));
        }
      });
  };
};

const handleAuthenticationSuccess = (dispatch, response, redirectUrl = null) => {
  // Always persist session locally on account.allorado.io
  localStorage.setItem("token", response.data.token);
  localStorage.setItem("username", response.data.username);
  localStorage.setItem("tokenExpiration", response.data.tokenExpiration);
  // Notify other account.allorado.io tabs that a login occurred
  localStorage.setItem("account_login", Date.now().toString());

  axios.defaults.headers.common["Authorization"] = "Bearer " + response.data.token;

  dispatch(authenticationSuccess(response.data.token, response.data.username));

  const currentTime = Math.floor((new Date()).getTime() / 1000);
  const tokenTimeLeft = response.data.tokenExpiration - currentTime;

  setTimeout(() => {
    dispatch(logout());
  }, tokenTimeLeft * 1000);

  // If redirectUrl is provided, also redirect back to the originating platform with the token
  if (redirectUrl) {
    const separator = redirectUrl.includes('?') ? '&' : '?';
    window.location.href = `${redirectUrl}${separator}token=${response.data.token}`;
  }
};

export const logout = () => {
  return (dispatch) => {
    localStorage.removeItem("token");
    localStorage.removeItem("username");
    localStorage.removeItem("tokenExpiration");
    // Notify other account.allorado.io tabs that a logout occurred
    localStorage.setItem("account_logout", Date.now().toString());
    delete axios.defaults.headers.common["Authorization"];
    dispatch({ type: actionTypes.AUTH_LOGOUT });
  };
};

export const generateSessionCode = (redirectUrl) => {
  return (dispatch) => {
    axios.post("/auth/generate-session-code", { redirectUrl })
      .then((response) => {
        const { sessionCode, redirectUrl: finalRedirectUrl } = response.data;
        
        // Redirect to originating platform with sessionCode
        const separator = finalRedirectUrl.includes("?") ? "&" : "?";
        window.location.href = `${finalRedirectUrl}${separator}code=${encodeURIComponent(sessionCode)}`;
      })
      .catch((err) => {
        console.error("Failed to generate session code:", err);
        // On error, redirect anyway 
        const separator = redirectUrl.includes("?") ? "&" : "?";
        window.location.href = `${redirectUrl}${separator}error=session_generation_failed`;
      });
  };
};

export const attemptSessionRestoration = () => {
  return (dispatch) => {
    const token = localStorage.getItem("token");
    const username = localStorage.getItem("username");
    const tokenExpiration = localStorage.getItem("tokenExpiration");

    if (token && username && tokenExpiration) {
      const currentTime = Math.floor((new Date()).getTime() / 1000);
      const tokenTimeLeft = tokenExpiration - currentTime;

      if (tokenTimeLeft > 0) {
        axios.defaults.headers.common["Authorization"] = "Bearer " + token;
        dispatch(authenticationSuccess(token, username));

        setTimeout(() => {
          dispatch(logout());
        }, tokenTimeLeft * 1000);
      } else {
        dispatch(logout());
      }
    }
  };
};
